import clsx from 'clsx';
import React from 'react';
import { Button, Dialog, Icon } from 'ui';

import { stripHtml } from 'core/utils/utils';
import PracticeStatusChip from './PracticeStatusChip';
import usePracticeLessonSelectionDialog from './usePracticeLessonSelectionDialog';

import type { PracticeLessonSelection } from '../../../types/practiceCourses';
import type { DecoratedLesson, DecoratedUnit } from './usePracticeLessonSelectionDialog';

import './PracticeLessonSelectionDialog.scss';

/**
 * PracticeLessonSelectionDialog - Popup 2 of the custom practice flow (TANAI-1869).
 * A selectable unit/lesson tree showing the student's progress: tri-state unit
 * checkboxes, mastery counters, status chips and a "prior units" warning banner.
 * All logic lives in `usePracticeLessonSelectionDialog`; this is pure JSX.
 */

type PracticeLessonSelectionDialogProps = {
  courseId: string;
  courseTitle?: string;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (selection: PracticeLessonSelection) => void;
};

function PracticeLessonSelectionDialog({
  courseId,
  courseTitle,
  isOpen,
  onClose,
  onConfirm,
}: Readonly<PracticeLessonSelectionDialogProps>): JSX.Element {
  const {
    decoratedUnits,
    handleClear,
    handleConfirm,
    handleClearSearch,
    handleSearchChange,
    handleSelectAll,
    handleToggleLesson,
    handleToggleUnit,
    handleToggleUnitExpansion,
    hasError,
    hasPracticableContent,
    hasUnits,
    isConfirmDisabled,
    isLessonSelected,
    isLoading,
    searchQuery,
    selectedCount,
    shouldShowPriorUnitsWarning,
    subtitle,
    t,
  } = usePracticeLessonSelectionDialog({ courseId, courseTitle, isOpen, onConfirm });

  function renderLesson(decoratedLesson: DecoratedLesson): JSX.Element {
    const { isDisabled, lesson } = decoratedLesson;
    const selected = isLessonSelected(lesson.lessonId);
    const lessonTitle = stripHtml(lesson.title);

    function handleLessonClick(): void {
      if (isDisabled) return;
      handleToggleLesson(lesson.lessonId);
    }

    return (
      <li className={clsx('practice-lesson-selection-dialog__lesson', isDisabled && 'practice-lesson-selection-dialog__lesson--disabled')} key={lesson.lessonId}>
        <button
          aria-checked={selected}
          aria-disabled={isDisabled}
          aria-label={lessonTitle}
          className={clsx(
            'practice-lesson-selection-dialog__checkbox',
            `practice-lesson-selection-dialog__checkbox--${selected ? 'checked' : 'unchecked'}`,
            isDisabled && 'practice-lesson-selection-dialog__checkbox--disabled'
          )}
          onClick={handleLessonClick}
          role="checkbox"
          type="button"
        >
          {selected && !isDisabled && <Icon color="inherit" name="check" size="sm" />}
        </button>
        <span className="practice-lesson-selection-dialog__lesson-name">{lessonTitle}</span>
        <span className="practice-lesson-selection-dialog__lesson-aside">
          {isDisabled ? (
            <span className="practice-lesson-selection-dialog__no-content-label">{t('practice_more_lesson_no_content')}</span>
          ) : (
            <PracticeStatusChip status={lesson.lessonStatus} />
          )}
        </span>
      </li>
    );
  }

  function renderUnit(decorated: DecoratedUnit): JSX.Element {
    const { checkState, isDisabled, isExpanded, isModulesOnly, lessons, masteredLessonCount, titlePlain, totalLessonCount, unit } = decorated;

    function handleCheckboxClick(): void {
      if (isDisabled) return;
      handleToggleUnit(unit);
    }

    function handleUnitMainClick(): void {
      if (isModulesOnly) {
        // A disabled modules-only unit has no lessons to expand and cannot be selected.
        if (isDisabled) return;
        handleToggleUnit(unit);
      } else {
        // A disabled unit with lessons stays expandable to reveal its structure.
        handleToggleUnitExpansion(unit.unitId);
      }
    }

    return (
      <li className="practice-lesson-selection-dialog__unit" key={unit.unitId}>
        <div className="practice-lesson-selection-dialog__unit-row">
          <button
            aria-checked={checkState === 'indeterminate' ? 'mixed' : checkState === 'checked'}
            aria-disabled={isDisabled}
            aria-label={titlePlain}
            className={clsx(
              'practice-lesson-selection-dialog__checkbox',
              `practice-lesson-selection-dialog__checkbox--${checkState}`,
              isDisabled && 'practice-lesson-selection-dialog__checkbox--disabled'
            )}
            onClick={handleCheckboxClick}
            role="checkbox"
            type="button"
          >
            {!isDisabled && checkState === 'checked' && <Icon color="inherit" name="check" size="sm" />}
            {!isDisabled && checkState === 'indeterminate' && <span className="practice-lesson-selection-dialog__checkbox-dash" />}
          </button>

          <button
            aria-disabled={isModulesOnly && isDisabled}
            aria-expanded={isModulesOnly ? undefined : isExpanded}
            className={clsx('practice-lesson-selection-dialog__unit-main', isDisabled && 'practice-lesson-selection-dialog__unit-main--disabled')}
            onClick={handleUnitMainClick}
            type="button"
          >
            <span className="practice-lesson-selection-dialog__unit-info">
              <span className="practice-lesson-selection-dialog__unit-name">{titlePlain}</span>
              {!isModulesOnly && (
                <span className="practice-lesson-selection-dialog__unit-counter">
                  {t('practice_more_unit_lessons_progress', { mastered: masteredLessonCount, total: totalLessonCount })}
                </span>
              )}
            </span>
            <span className="practice-lesson-selection-dialog__unit-aside">
              {isDisabled ? (
                <span className="practice-lesson-selection-dialog__no-content-label">{t('practice_more_unit_no_content')}</span>
              ) : (
                <PracticeStatusChip status={unit.unitStatus} />
              )}
              {!isModulesOnly && (
                <span className="practice-lesson-selection-dialog__chevron">
                  <Icon name={isExpanded ? 'chevron-up' : 'chevron-down'} size="sm" />
                </span>
              )}
            </span>
          </button>
        </div>

        {!isModulesOnly && isExpanded && <ul className="practice-lesson-selection-dialog__lessons">{lessons.map(renderLesson)}</ul>}
      </li>
    );
  }

  function renderTree(): JSX.Element {
    if (isLoading) {
      return <div className="practice-lesson-selection-dialog__state">{t('practice_more_lessons_loading')}</div>;
    }
    if (hasError) {
      return <div className="practice-lesson-selection-dialog__state">{t('practice_more_lessons_error')}</div>;
    }
    if (!hasUnits) {
      return <div className="practice-lesson-selection-dialog__state">{t('practice_more_no_units')}</div>;
    }
    if (!hasPracticableContent) {
      return <div className="practice-lesson-selection-dialog__state">{t('practice_more_no_practicable_content')}</div>;
    }
    if (decoratedUnits.length === 0) {
      return <div className="practice-lesson-selection-dialog__state">{t('practice_more_no_lessons_results')}</div>;
    }
    return <ul className="practice-lesson-selection-dialog__tree">{decoratedUnits.map(renderUnit)}</ul>;
  }

  return (
    <Dialog
      buttonTextSuccess={t('practice_more_generate_session')}
      className="practice-lesson-selection-dialog"
      customFooterLeft={
        <span className="practice-lesson-selection-dialog__footer-count">
          {selectedCount === 1 ? t('lessons_chosen', { count: selectedCount }) : t('lessons_chosen_plural', { count: selectedCount })}
        </span>
      }
      isDisabledSuccess={isConfirmDisabled}
      isOpen={isOpen}
      onClose={onClose}
      onSuccess={handleConfirm}
      size="l"
      title={t('practice_more_lessons_dialog_title')}
    >
      <div className="practice-lesson-selection-dialog__content">
        <div className="practice-lesson-selection-dialog__section">
          <span className="practice-lesson-selection-dialog__section-label">{t('practice_more_syllabus_selection')}</span>
          {subtitle && <span className="practice-lesson-selection-dialog__section-subtitle">{subtitle}</span>}
        </div>

        <div className="practice-lesson-selection-dialog__box">
          <div className="practice-lesson-selection-dialog__search">
            <input
              className="practice-lesson-selection-dialog__search-input"
              onChange={handleSearchChange}
              placeholder={t('practice_more_search_placeholder')}
              type="text"
              value={searchQuery}
            />
            {searchQuery ? (
              <Button className="practice-lesson-selection-dialog__search-icon" variant="plain" isIconOnly leftIcon="close" ariaLabel={t('practice_more_clear')} onClick={handleClearSearch} />
            ) : (
              <span className="practice-lesson-selection-dialog__search-icon">
                <Icon name="search" size="lg" />
              </span>
            )}
          </div>

          {renderTree()}

          <div className="practice-lesson-selection-dialog__actions">
            <Button onClick={handleSelectAll} size="xs" text={t('practice_more_select_all')} variant="primary-outline" isFullWidth />
            <Button isDisabled={isConfirmDisabled} onClick={handleClear} size="xs" text={t('practice_more_clear')} variant="primary-ghost" isFullWidth />
          </div>
        </div>

        {shouldShowPriorUnitsWarning && (
          <div className="practice-lesson-selection-dialog__warning">
            <span aria-hidden="true" className="practice-lesson-selection-dialog__warning-mascot" />
            <span className="practice-lesson-selection-dialog__warning-text">{t('practice_more_prior_units_warning')}</span>
          </div>
        )}
      </div>
    </Dialog>
  );
}

export default PracticeLessonSelectionDialog;
